import React, { useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

interface DocumentUploadFieldProps {
    label: string;
    value?: string | File;
    onChange: (file: File | null) => void;
    accept?: string;
    required?: boolean;
    maxSizeMB?: number;
    placeholder?: string;
    helperText?: string;
    error?: string;
    disabled?: boolean;
}

const DocumentUploadField: React.FC<DocumentUploadFieldProps> = ({
    label,
    value,
    onChange,
    accept = 'image/*,.pdf',
    required = false,
    maxSizeMB = 5,
    placeholder = 'Click to upload or drag and drop',
    helperText,
    error,
    disabled = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [preview, setPreview] = useState<string | null>(null);

    // Generate preview for images
    React.useEffect(() => {
        if (value instanceof File) {
            if (value.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreview(reader.result as string);
                };
                reader.readAsDataURL(value);
            } else {
                setPreview(null);
            }
        } else if (typeof value === 'string' && value.startsWith('http')) {
            setPreview(value);
        } else {
            setPreview(null);
        }
    }, [value]);

    const validateFile = (file: File): boolean => {
        // Check file size
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            toast.error(`File size must be less than ${maxSizeMB}MB`);
            return false;
        }

        // Check file type
        const acceptedTypes = accept.split(',').map(t => t.trim());
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        const mimeType = file.type;

        const isAccepted = acceptedTypes.some(type => {
            if (type.startsWith('.')) {
                return fileExtension === type;
            }
            if (type.includes('/*')) {
                const baseType = type.split('/')[0];
                return mimeType.startsWith(baseType + '/');
            }
            return mimeType === type;
        });

        if (!isAccepted) {
            toast.error(`File type not accepted. Allowed: ${accept}`);
            return false;
        }

        return true;
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && validateFile(file)) {
            onChange(file);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);

        const file = event.dataTransfer.files?.[0];
        if (file && validateFile(file)) {
            onChange(file);
        }
    };

    const handleRemove = () => {
        onChange(null);
        setPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const getFileName = (): string | null => {
        if (value instanceof File) {
            return value.name;
        }
        if (typeof value === 'string') {
            return value.split('/').pop() || value;
        }
        return null;
    };

    const fileName = getFileName();
    const isPDF = fileName?.toLowerCase().endsWith('.pdf');

    return (
        <div className="space-y-2">
            <Label>
                {label}
                {required && <span className="text-destructive ml-1">*</span>}
            </Label>

            {/* Upload Area */}
            <div
                className={`
          border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer
          ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary hover:bg-muted/50'}
          ${error ? 'border-destructive' : ''}
        `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={disabled ? undefined : handleClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={disabled}
                />

                {value ? (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Preview or Icon */}
                            {preview && !isPDF ? (
                                <img
                                    src={preview}
                                    alt="Preview"
                                    className="w-16 h-16 object-cover rounded border"
                                />
                            ) : (
                                <div className="w-16 h-16 bg-muted rounded border flex items-center justify-center">
                                    {isPDF ? (
                                        <FileText className="w-8 h-8 text-muted-foreground" />
                                    ) : (
                                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                                    )}
                                </div>
                            )}

                            {/* File Info */}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{fileName}</p>
                                {value instanceof File && (
                                    <p className="text-xs text-muted-foreground">
                                        {(value.size / 1024).toFixed(1)} KB
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Remove Button */}
                        {!disabled && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove();
                                }}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mb-1">{placeholder}</p>
                        <p className="text-xs text-muted-foreground">
                            {accept} (max {maxSizeMB}MB)
                        </p>
                    </div>
                )}
            </div>

            {/* Helper Text or Error */}
            {error ? (
                <p className="text-sm text-destructive">{error}</p>
            ) : helperText ? (
                <p className="text-sm text-muted-foreground">{helperText}</p>
            ) : null}
        </div>
    );
};

export default DocumentUploadField;
